import { computed, inject, provide, ref, Ref } from "vue"
import { Rsvp, RsvpReply } from "suus-api"
import { rsvpApi } from "store/api"

const RSVP_LOAD_LIMIT = 12

export interface RSVPConcern {
  // Counts & Arrays
  countExpectedByReply: (reply: RsvpReply) => Readonly<Ref<number>> // The expected count is the original count + a counter that tracks local changes
  guestsCount: Readonly<Ref<number>> // The guests count is the sum of all guests that are attending
  totalAttendsCount: Readonly<Ref<number>> // The total attend count is the sum of all attendees and guests
  countByReplyAndSection: (
    reply: RsvpReply,
    section: RsvpSection
  ) => Readonly<Ref<number>> // The count of attendees by reply and section e.g. reply: RSVPReply.Attend, section: "coetans"
  byReply: (reply: RsvpReply) => Readonly<Ref<Rsvp[]>> // All RSVPs by reply
  byReplyAndSection: (
    reply: RsvpReply,
    section: RsvpSection
  ) => Readonly<Ref<Rsvp[]>> // All RSVPs by reply and section

  // Loading and Error State
  allLoaded: Readonly<Ref<{ [key in RsvpReply]: boolean }>>
  loadingMore: Ref<boolean>
  loading: Ref<boolean>
  error: Ref<string | null>

  // Methods
  preloadRSVPs: () => void
  createRSVP: (
    personalRecordId: number,
    reply: RsvpReply,
    guestsCount?: number
  ) => Promise<Rsvp>
  updateRSVP: (
    personalRecordId: number,
    reply: RsvpReply,
    guestsCount?: number
  ) => Promise<Rsvp>
  deleteRSVP: (personalRecordId: number) => Promise<void>
  loadMore: (reply: RsvpReply) => Promise<void>
}

/**
 * Used for reloading the RSVP counts from the server for example, so the concern can refresh the data.
 */
type LoadRSVPCountsFunction = () => Promise<{
  attendsCount: number
  guestsCount: number
  declinesCount: number
  pendingsCount: number
}>

export type RsvpSection = "coetans" | "nonCoetans"

function RSVPConcern(
  eventId: string,
  loadRSVPCounts: LoadRSVPCountsFunction
): RSVPConcern {
  const originalCounts = ref<{
    [key in RsvpReply]: number
  }>({
    [RsvpReply.Attend]: 0,
    [RsvpReply.Decline]: 0,
    [RsvpReply.Pending]: 0,
  })

  const expectedCounts = ref<{
    [key in RsvpReply]: number
  }>({
    [RsvpReply.Attend]: 0,
    [RsvpReply.Decline]: 0,
    [RsvpReply.Pending]: 0,
  })

  const guestsCount = ref(0)

  const initialCountLoad = loadRSVPCounts().then((counts) => {
    originalCounts.value = {
      [RsvpReply.Attend]: counts.attendsCount,
      [RsvpReply.Decline]: counts.declinesCount,
      [RsvpReply.Pending]: counts.pendingsCount,
    }
    guestsCount.value = counts.guestsCount
    return counts
  })

  function countExpectedByReply(reply: RsvpReply) {
    return computed(
      () => originalCounts.value[reply] + expectedCounts.value[reply]
    )
  }

  const totalAttendsCount = computed(
    () =>
      originalCounts.value[RsvpReply.Attend] +
      expectedCounts.value[RsvpReply.Attend] +
      guestsCount.value
  )

  function countByReplyAndSection(reply: RsvpReply, section: RsvpSection) {
    const coetans = byReplyAndSection(reply, "coetans")
    const expectedCount = countExpectedByReply(reply)
    return computed(() => {
      if (section === "coetans") {
        return coetans.value.length
      } else {
        return expectedCount.value - coetans.value.length
      }
    })
  }

  const loadedRsvps = ref<Rsvp[]>([])

  function byReply(reply: RsvpReply) {
    return computed(
      () => loadedRsvps.value.filter((rsvp) => rsvp.reply === reply) as Rsvp[]
    )
  }

  function byReplyAndSection(reply: RsvpReply, section: RsvpSection) {
    const rsvps = byReply(reply)
    const filter = (rsvp: Rsvp) =>
      section === "coetans" ? rsvp.is_coetan : !rsvp.is_coetan
    return computed(() => {
      return rsvps.value.filter(filter) as Rsvp[]
    })
  }

  const loading = ref(false)
  const loadingMore = ref(false)
  const error: Ref<string | null> = ref(null)

  const allLoaded = ref<{ [key in RsvpReply]: boolean }>({
    [RsvpReply.Attend]: false,
    [RsvpReply.Decline]: false,
    [RsvpReply.Pending]: false,
  })
  const loadMore = async (reply: RsvpReply) => {
    if (allLoaded.value[reply]) return
    loadingMore.value = true
    try {
      const rsvpResponse = await rsvpApi.getRsvps(
        eventId,
        byReply(reply).value.length + expectedCounts.value[reply],
        RSVP_LOAD_LIMIT,
        undefined,
        undefined,
        reply
      )
      loadedRsvps.value = [...loadedRsvps.value, ...rsvpResponse.data]
      const allLoadedValue =
        rsvpResponse.status === 204 ||
        rsvpResponse.data.length < RSVP_LOAD_LIMIT ||
        byReply(reply).value.length >= countExpectedByReply(reply).value
      allLoaded.value = {
        ...allLoaded.value,
        [reply]: allLoadedValue,
      }
    } finally {
      loadingMore.value = false
    }
  }

  const preloadRSVPs = async () => {
    await initialCountLoad
    error.value = null
    loading.value = true
    try {
      await Promise.all([
        loadMore(RsvpReply.Attend),
        loadMore(RsvpReply.Decline),
        loadMore(RsvpReply.Pending),
      ])
    } catch (_error) {
      if (_error instanceof Error) {
        error.value = _error.message
      } else {
        error.value = "An error occurred while fetching RSVPs."
      }
      console.error("An error occurred while fetching RSVPs.", _error)
    } finally {
      loading.value = false
    }
  }

  const createRSVP = async (
    personalRecordId: number,
    reply: RsvpReply,
    _guestsCount = 0
  ) => {
    const response = await rsvpApi.createRsvp(
      eventId,
      personalRecordId.toString(),
      {
        reply: reply,
        ...(_guestsCount !== undefined && {
          guests_count: _guestsCount,
        }),
      }
    )
    loadedRsvps.value = [response.data, ...loadedRsvps.value]
    // update expectations
    expectedCounts.value[reply] = expectedCounts.value[reply] + 1
    if (reply === RsvpReply.Attend) {
      guestsCount.value = guestsCount.value + _guestsCount
    }
    return response.data
  }

  const updateRSVP = async (
    personalRecordId: number,
    reply: RsvpReply,
    _guestsCount?: number
  ) => {
    const response = await rsvpApi.updateRsvp(
      eventId,
      personalRecordId.toString(),
      {
        reply: reply,
        guests_count: _guestsCount,
      }
    )
    const foundIndex = loadedRsvps.value.findIndex(
      (rsvp) => rsvp.personal_record_id === personalRecordId
    )
    if (foundIndex !== -1) {
      const previousReply = loadedRsvps.value[foundIndex].reply
      const previousGuestsCount = loadedRsvps.value[foundIndex].guests_count
      if (previousReply !== reply) {
        expectedCounts.value[previousReply] =
          expectedCounts.value[previousReply] - 1
        expectedCounts.value[reply] = expectedCounts.value[reply] + 1
      }
      loadedRsvps.value[foundIndex] = response.data
      loadedRsvps.value = [...loadedRsvps.value] // I am unsure if this is necessary, reactivity might not pick up the change
      if (reply === RsvpReply.Attend) {
        const guestsDiff = (_guestsCount ?? 0) - previousGuestsCount
        guestsCount.value = guestsCount.value + guestsDiff
      }
    } else {
      // Attention: I cant know if the RSVP switched from another reply to this one, means the expected counter might be wrong
      loadedRsvps.value = [response.data, ...loadedRsvps.value]
    }
    return response.data
  }

  const deleteRSVP = async (personalRecordId: number) => {
    await rsvpApi.deleteRsvp(eventId, personalRecordId.toString())
    const foundIndex = loadedRsvps.value.findIndex(
      (rsvp) => rsvp.personal_record_id === personalRecordId
    )
    if (foundIndex !== -1) {
      const previousReply = loadedRsvps.value[foundIndex].reply
      const previousRsvp = loadedRsvps.value[foundIndex]
      loadedRsvps.value.splice(foundIndex, 1)
      loadedRsvps.value = [...loadedRsvps.value] // I am unsure if this is necessary, reactivity might not pick up the change
      // update expectations
      expectedCounts.value[previousReply] =
        expectedCounts.value[previousReply] - 1
      if (previousReply === RsvpReply.Attend) {
        guestsCount.value = guestsCount.value - previousRsvp.guests_count
      }
    }
  }

  return {
    countExpectedByReply,
    guestsCount,
    totalAttendsCount,
    countByReplyAndSection,
    byReply,
    byReplyAndSection,
    allLoaded,
    loadingMore,
    loading,
    error,
    preloadRSVPs,
    createRSVP,
    updateRSVP,
    deleteRSVP,
    loadMore,
  }
}

/**
 * This should be called in the top-level component, where the Context should be set up.
 * This probably shouldn't be called multiple times, as it will create multiple contexts.
 */
export function provideRSVPContext(
  eventId: string,
  loadRSVPCounts: LoadRSVPCountsFunction
): RSVPConcern {
  const concern = RSVPConcern(eventId, loadRSVPCounts)
  provide("RSVPConcern", concern)
  return concern
}

/**
 * This should be called in any component that needs to access the RSVP context.
 * It expects that the context has been set up by `provideRSVPContext`.
 */
export function useRSVPContext(): RSVPConcern {
  const concern = inject("RSVPConcern", null) as RSVPConcern
  if (concern === null) {
    throw new Error(
      "RSVPConcern was not found in 'useRSVPContext'. Did you forget to provide a context with 'provideRSVPContext'?"
    )
  }
  return concern
}

/**
 * This should be called in any component that needs to access the RSVP context, IF THERE IS ONE.
 * If no context is found, it will return null.
 */
export function useRSVPContextOptional(): RSVPConcern | null {
  return inject("RSVPConcern", null) as RSVPConcern | null
}
